import { useAuthorization } from 'dx-sdk/build/authorization';
import {
  Accordion,
  Button,
  CustomerJourneyDiamond,
  Hero,
  IconCard,
  ItemsGrid,
  LinkArrow,
  NextStepCard,
  Picture,
  PlannerField,
  PlannerFields,
  PlannerQuestion,
  PlannerQuestionnaire,
  TextInput,
  Typography,
} from 'dx-sdk/build/components';
import RadioQuestion from 'dx-sdk/build/components/PlannerQuestion/components/Radio';
import {
  Arrow,
  Clock,
  House,
  Mountains,
  Pencil,
  PencilAndPaper,
  PieGraph,
} from 'dx-sdk/build/icons';
import { PieChart } from 'dx-sdk/build/icons/illustrations';
import { Theme as ThemeProvider } from 'dx-sdk/build/providers';
import { formatDealerName } from 'pubweb-smokey/dist/utils/formatters';
import React, { useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { InView } from 'react-intersection-observer';
import { Link, Redirect, useHistory, useLocation } from 'react-router-dom';
import App from '../../../App';
import { smoothScroll } from '../../../components/Shared/SmokeyTransitional/utils/utils';
import { DXAccountContext } from '../../../contexts/DXAccountContext';
import useWindowSize from '../../../hooks/useWindowSize';
import book from '../../../images/portal/icons/book.svg';
import clock from '../../../images/portal/icons/clock.svg';
import lightbulb from '../../../images/portal/icons/lightbulb.svg';
import PlannerJourneyHeroBG from '../../../images/portal/planner/journey/PlannerJourneyHeroBG.jpg';
import Loading from '../../../images/svg/icons/Loading';
import * as dealerService from '../../../services/dealerService';
import * as homesService from '../../../services/homesService';
import { fireSegmentEvent, parseInitialPropsContext } from '../../../utils';
import HomeBuyingJourneyIcon from '../../components/PlannerIcons/HomeBuyingJourneyIcon';
import {
  buyerSituationAnswer,
  portalIconProgress,
  setMoveDateFormat,
} from '../../utils/PortalUtils';
import HomeBuyingJourneyStyles from './HomeBuyingJourney.styled';

const HomeBuyingJourney = ({ dealer, homes }) => {
  const [width, height] = useWindowSize();
  const [showDiamond, setShowDiamond] = useState(false);
  const dxContext = useContext(DXAccountContext);
  const { isAuthenticated, isLoading } = useAuthorization();
  const [formIsSaved, setFormIsSaved] = useState(false);
  const [activeQuestion, setActiveQuestion] = useState(1);
  const [monthYearButtonDisabled, setMonthYearButtonDisabled] = useState(false);
  const [monthError, setMonthError] = useState('');
  const [yearError, setYearError] = useState('');
  const [activeYear, setActiveYear] = useState(null);
  const [monthList, setMonthList] = useState([{ label: '', value: 0 }]);
  const [updatedUserProfile, setUpdatedUserProfile] = useState(null);
  const [updatedUser, setUpdatedUser] = useState({
    userId: '',
  });
  const location = useLocation();
  const history = useHistory();
  const [journeyFormSavedHCs, setJourneyFormSavedHCs] = useState([]);
  const [journeyFormSavedProfileIds, setJourneyFormSavedProfileIds] = useState(
    []
  );

  useEffect(() => {
    const items = JSON.parse(localStorage.getItem('journey_fs'));
    if (items) {
      setJourneyFormSavedHCs(items.homeCenters);
      setJourneyFormSavedProfileIds(items.profileIds);
    }
  }, []);

  useEffect(() => {
    if (journeyFormSavedHCs && journeyFormSavedHCs.length) {
      let filteredHCList = [...new Set(journeyFormSavedHCs)];
      let filteredProfileIdList = [...new Set(journeyFormSavedProfileIds)];
      localStorage.setItem(
        'journey_fs',
        JSON.stringify({
          homeCenters: filteredHCList,
          profileIds: filteredProfileIdList,
        })
      );
    }
  }, [journeyFormSavedHCs, journeyFormSavedProfileIds]);

  useEffect(() => {
    let lotsStored =
      journeyFormSavedHCs && journeyFormSavedHCs.length
        ? journeyFormSavedHCs?.map((i) => i)
        : [];
    let profileIdsStored =
      journeyFormSavedProfileIds && journeyFormSavedProfileIds.length
        ? journeyFormSavedProfileIds?.map((i) => i)
        : [];
    if (
      !location?.state?.activeQuestionLink &&
      lotsStored.includes(dealer?.dealerId) &&
      profileIdsStored.includes(updatedUserProfile?.profileId)
    ) {
      setFormIsSaved(true);
    }
  }, [
    journeyFormSavedHCs,
    journeyFormSavedProfileIds,
    updatedUserProfile?.profileId,
  ]);

  useEffect(() => {
    setShowDiamond(true);
    if (location?.state?.activeQuestionLink) {
      history.replace({ state: { dealer, homes } }) +
        setFormIsSaved(false) +
        setActiveQuestion(location?.state?.activeQuestionLink) +
        smoothScroll('#plannerSection', -20);
    }
  }, []);

  const journeyPlannerProgressObj =
    dxContext?.state?.account?.plannerProgress?.find(
      (item) => item.section == 'Home Buying Journey'
    );

  const portalIcon = portalIconProgress(
    journeyPlannerProgressObj?.percentCompleted || 0
  );

  useEffect(() => {
    if (
      dxContext?.state?.account &&
      dxContext?.state?.account?.profile &&
      !updatedUser.userId
    ) {
      setUpdatedUserProfile(dxContext?.state?.account.profile);
      setUpdatedUser({
        userId: dxContext?.state?.account.userId,
      });
      setActiveYear(dxContext?.state?.account?.profile?.targetMoveDateYear);
    }
  }, [dxContext?.state?.account]);

  const handleProfileFieldUpdate = (key) => (value) => {
    const newProfileObj = {
      ...updatedUserProfile,
      [key]: value,
    };

    if (key === 'buyerSituation') {
      dxContext?.actions?.markContentComplete(
        journeyPlannerProgressObj,
        'home_buying_journey_question_1'
      );
      setUpdatedUserProfile(newProfileObj);
      dxContext?.actions?.updateProfileRetail(newProfileObj);
    } else if (key === 'targetMoveDateMonth' || key === 'targetMoveDateYear') {
      setUpdatedUserProfile(newProfileObj);
      dxContext?.actions?.updateProfileRetail(newProfileObj);
    } else {
      return null;
    }
  };

  useEffect(() => {
    let scrollbar = width - document.body.clientWidth;
    document.body.style.setProperty('--scrollbarWidth', `${scrollbar}px`);
  }, [width]);

  useEffect(() => {
    if (
      updatedUserProfile?.targetMoveDateYear &&
      updatedUserProfile?.targetMoveDateMonth
    ) {
      setYearError(null);
      setMonthError(null);
      dxContext?.actions?.markContentComplete(
        journeyPlannerProgressObj,
        'home_buying_journey_question_2'
      );
    }
  }, [
    updatedUserProfile?.targetMoveDateYear,
    updatedUserProfile?.targetMoveDateMonth,
  ]);

  const BackLink = () => {
    return (
      <LinkArrow
        isReversed
        components={{ Root: Link }}
        componentProps={{
          to: { pathname: '/portal/planner', state: { dealer, homes } },
        }}
      >
        Back to MyHome Planner
      </LinkArrow>
    );
  };

  const BudgetLink = ({ children }) => {
    return (
      <NextStepCard
        {...children}
        components={{
          Link: () => (
            <Link
              className="NextStepCard-link"
              to={{ pathname: '/portal/budget', state: { dealer, homes } }}
            >
              <Arrow />
            </Link>
          ),
        }}
      />
    );
  };

  const CustomAction = (value, questionNumber) => {
    return (
      <div
        className="DXButton DXButton-root"
        onClick={() => {
          setFormIsSaved(false) + setActiveQuestion(questionNumber);
        }}
      >
        {value ? (
          <>
            <Pencil /> <span className="hide-on-mobile">Edit</span>
          </>
        ) : (
          <>
            <PencilAndPaper />
            <span className="hide-on-mobile">COMPLETE YOUR ANSWER</span>
          </>
        )}
      </div>
    );
  };

  const accordionContent = [
    {
      title: 'How can my local home center help me?',
      content: (
        <Typography>
          The team at your local home center is there to guide you as you move
          through your home buying journey. They can answer questions you may
          have, help with finding land, explain paperwork and discuss upgrade
          and customization options.
        </Typography>
      ),
    },
    {
      title: 'Do I need to complete the planner steps in order?',
      content: (
        <Typography>
          Complete the planner steps in the order most comfortable to you. Start
          with what you know, such as home needs or budget, and work through the
          other steps next. Feel free to go back and edit your answers as often
          as you like.
        </Typography>
      ),
    },
    {
      title: 'I don’t own land yet. What should I do?',
      content: (
        <Typography>
          First, consider your options. If you are not ready to purchase land,
          consider renting land in a community. If you are interested in
          purchasing land, reach out to your local home center. They can help
          you find available properties already zoned for manufactured homes, as
          well as homes that are already on land.
        </Typography>
      ),
    },
    {
      title: 'How do I know how much home I can afford?',
      content: (
        <Typography>
          Start by using our calculator to factor your household income, monthly
          debts and the amount of savings you have available for a down payment.
        </Typography>
      ),
    },
  ];

  useEffect(() => {
    const dateobj = new Date();
    const currentYear = dateobj.getFullYear().toString();
    const currentMonth = dateobj.getMonth();
    const months = Array.from({ length: 12 }, (item, i) => {
      return new Date(0, i).toLocaleString('en-US', { month: 'long' });
    });

    let finalMonthArray;

    if (activeYear == currentYear) {
      let availableMonths = months.slice(currentMonth);

      finalMonthArray = months.map((item, i) => {
        return {
          label: item,
          value: i + 1,
          disabled: !availableMonths.includes(item) ? true : false,
        };
      });

      let targetDateFind = finalMonthArray.find(
        ({ value }) => value === updatedUserProfile?.targetMoveDateMonth
      );

      if (targetDateFind?.disabled) {
        let firstNonDisabled = finalMonthArray.find(
          ({ disabled }) => disabled === false
        );
        handleProfileFieldUpdate('targetMoveDateMonth')(
          parseFloat(firstNonDisabled?.value)
        );
      }
    } else {
      finalMonthArray = months.map((item, i) => {
        return {
          label: item,
          value: i + 1,
        };
      });
    }

    setMonthList(finalMonthArray);
  }, [activeYear]);

  const yearArray = () => {
    let date = new Date(),
      years = [],
      year = date.getFullYear();

    for (var i = year; i < year + 10; i++) {
      years.push({ label: i, value: i });
    }

    return years;
  };

  const TriggerBtn = () => {
    return (
      <button
        className="DXButton DXButton-root DX-TriggerBtn PlannerQuestionnaire-trigger"
        onClick={() => {
          if (
            updatedUserProfile?.targetMoveDateYear &&
            !updatedUserProfile?.targetMoveDateMonth
          ) {
            setMonthError('Select Target Month');
          } else if (
            updatedUserProfile?.targetMoveDateMonth &&
            !updatedUserProfile?.targetMoveDateYear
          ) {
            setYearError('Select Target Year');
          } else {
            setYearError(null);
            setMonthError(null);
            setFormIsSaved(true);
          }
        }}
      >
        SAVE <div className="background"></div>
      </button>
    );
  };

  if (!isAuthenticated && !isLoading) {
    return <Redirect to="/myhome-account-login" />;
  }

  return (
    <ThemeProvider theme={{}}>
      <App dealer={dealer} homes={homes}>
        <HomeBuyingJourneyStyles>
          <Helmet>
            <title>
              Home Buying Journey | {formatDealerName(dealer?.dealerName)}
            </title>
            <meta name="robots" content="noindex" />
          </Helmet>

          {dxContext?.state?.globalLoading ? (
            <div className="loading-screen-panel">
              <Loading width="150px" />
            </div>
          ) : (
            <>
              <Hero
                components={{
                  Backdrop: Picture,
                }}
                componentsProps={{
                  Backdrop: {
                    src: PlannerJourneyHeroBG,
                  },
                  Container: {
                    style: {
                      margin: '0px auto',
                    },
                  },
                }}
              >
                <IconCard
                  heading="The Home Buying Journey"
                  subheading="Back to MyHome Planner"
                  icon={
                    <HomeBuyingJourneyIcon
                      dashArray={portalIcon?.dashArray}
                      dashOffset={portalIcon?.dashOffset}
                      opacity={portalIcon?.opacity}
                      showStar={portalIcon?.showStar}
                    />
                  }
                  iconOverlay
                  components={{
                    Subheading: BackLink,
                  }}
                >
                  <Typography>
                    We're here to help you understand each step of the home
                    buying journey, so you can feel more confident about what's
                    ahead. Learn more about how your land, budget and home needs
                    can all impact your move-in timeline. If you have questions,
                    feel free to reach out to a home consultant for help.
                  </Typography>
                </IconCard>
              </Hero>
              <section className="recommendations-section">
                <div className="container">
                  <ItemsGrid
                    heading="Recommendations to explore on this page:"
                    items={[
                      {
                        children: (
                          <span
                            onClick={() => smoothScroll('#plannerSection', -20)}
                          >
                            <img src={clock} alt="clock icon" />
                            Tell Us About You
                          </span>
                        ),
                      },
                      {
                        children: (
                          <span
                            onClick={() =>
                              smoothScroll(
                                '#customerJourneyDiamondSection',
                                -20
                              )
                            }
                          >
                            <img src={book} alt="book icon" />
                            Read How It Works
                          </span>
                        ),
                      },
                      {
                        children: (
                          <span
                            onClick={() =>
                              smoothScroll('#accordionSection', -20)
                            }
                          >
                            <img src={lightbulb} alt="lightbulb icon" />
                            Explore FAQs
                          </span>
                        ),
                      },
                    ]}
                    components={{
                      Item: Button,
                    }}
                    componentsProps={{
                      Heading: {
                        variant: 'p',
                      },
                      Item: {
                        variant: 'outline',
                        color: '#4E5255',
                        size: 'small',
                      },
                    }}
                  />
                </div>
              </section>
              <section className="planner-section" id="plannerSection">
                <div className="container">
                  <div className="planner-box">
                    {updatedUserProfile !== null ? (
                      <>
                        {!formIsSaved ? (
                          <PlannerQuestionnaire
                            activeQuestion={activeQuestion}
                            onSave={() => {
                              setFormIsSaved(true) +
                                setJourneyFormSavedHCs((hc) => [
                                  ...hc,
                                  dealer?.dealerId,
                                ]) +
                                setJourneyFormSavedProfileIds((p) => [
                                  ...p,
                                  updatedUserProfile?.profileId,
                                ]);
                            }}
                            handlers={{
                              onClick: (e) => {
                                smoothScroll('.planner-box', -20) +
                                  setActiveQuestion(activeQuestion + 1);
                              },
                              onPrevClick: (e) => {
                                smoothScroll('.planner-box', -20) +
                                  setActiveQuestion(activeQuestion - 1);
                              },
                            }}
                            components={{
                              Trigger:
                                activeQuestion === 2 &&
                                ((updatedUserProfile?.targetMoveDateMonth &&
                                  !updatedUserProfile?.targetMoveDateYear) ||
                                  (updatedUserProfile?.targetMoveDateYear &&
                                    !updatedUserProfile?.targetMoveDateMonth))
                                  ? TriggerBtn
                                  : null,
                            }}
                            children={[
                              <PlannerQuestion
                                type="custom"
                                heading="What are you looking for in a home?"
                                note="Understanding what you need will help us recommend the right home options for you."
                              >
                                <RadioQuestion
                                  values={[
                                    {
                                      label:
                                        'I’m looking to move in as soon as possible.',
                                      value: 'time_constrained',
                                    },
                                    {
                                      label: 'I’m looking for my first home.',
                                      value: 'first_time_buyer',
                                    },
                                    {
                                      label:
                                        'I’m looking for a new home that better fits my needs.',
                                      value: 'second_home',
                                    },
                                    {
                                      label: 'I’m looking for my forever home.',
                                      value: 'forever_home',
                                    },
                                  ]}
                                  defaultValue={
                                    updatedUserProfile?.buyerSituation || null
                                  }
                                  handlers={{
                                    onChange:
                                      handleProfileFieldUpdate(
                                        'buyerSituation'
                                      ),
                                  }}
                                />
                              </PlannerQuestion>,
                              <PlannerQuestion
                                type="custom"
                                className="select-box-question"
                                heading="When are you hoping to buy your new home?"
                                note="If you are unsure of a date, a rough estimate will work. You can always adjust this in the future!"
                                children={[
                                  <div className="selectbox-wrap">
                                    <TextInput
                                      name="monthselectbox"
                                      label="Month"
                                      color="#4E5255"
                                      variant="outline"
                                      fullwidth
                                      id="monthSelect"
                                      vanity
                                      note={monthError || null}
                                      value={
                                        updatedUserProfile?.targetMoveDateMonth ||
                                        null
                                      }
                                      values={monthList}
                                      handlers={{
                                        onChange: (value) =>
                                          handleProfileFieldUpdate(
                                            'targetMoveDateMonth'
                                          )(parseFloat(value)),
                                      }}
                                    />
                                    <TextInput
                                      name="yearselectbox"
                                      label="Year"
                                      color="#4E5255"
                                      variant="outline"
                                      fullwidth
                                      id="yearSelect"
                                      vanity
                                      note={yearError || null}
                                      value={
                                        updatedUserProfile?.targetMoveDateYear ||
                                        null
                                      }
                                      values={yearArray()}
                                      handlers={{
                                        onChange: (value) =>
                                          handleProfileFieldUpdate(
                                            'targetMoveDateYear'
                                          )(parseFloat(value)) +
                                          setActiveYear(value),
                                      }}
                                    />
                                  </div>,
                                ]}
                              ></PlannerQuestion>,
                            ]}
                          />
                        ) : (
                          <PlannerFields
                            heading="Your Home Journey"
                            subheading="Your Answers"
                          >
                            <PlannerField
                              heading="Reason for looking"
                              answer={buyerSituationAnswer(
                                updatedUserProfile?.buyerSituation
                              )}
                              components={{
                                Action: () =>
                                  CustomAction(
                                    updatedUserProfile?.buyerSituation,
                                    1
                                  ),
                              }}
                            />
                            <PlannerField
                              heading="Target purchase date"
                              answer={setMoveDateFormat(
                                updatedUserProfile?.targetMoveDateMonth,
                                updatedUserProfile?.targetMoveDateYear
                              )}
                              components={{
                                Action: () =>
                                  CustomAction(
                                    updatedUserProfile?.targetMoveDateMonth &&
                                      updatedUserProfile?.targetMoveDateYear,
                                    2
                                  ),
                              }}
                            />
                          </PlannerFields>
                        )}
                      </>
                    ) : (
                      <div className="planner-loading-screen">
                        <Loading width="100px" />
                      </div>
                    )}
                  </div>
                </div>
              </section>
              {showDiamond && (
                <InView
                  triggerOnce
                  as="section"
                  className="customer-journey-diamond-section"
                  id="customerJourneyDiamondSection"
                  threshold={0.3}
                  onChange={(inView, entry) =>
                    inView
                      ? dxContext?.actions?.markContentComplete(
                          journeyPlannerProgressObj,
                          'home_buying_journey_diamond'
                        ) +
                        dxContext?.actions?.markContentComplete(
                          journeyPlannerProgressObj,
                          'home_buying_journey_home_types'
                        )
                      : null
                  }
                >
                  <div className="container">
                    <CustomerJourneyDiamond
                      heading="Bringing It All Together"
                      subheading={
                        <>
                          <p>
                            Budget, land, timeline and home needs can all impact
                            your home buying journey. It is important to
                            prioritize which of these matters most to you.
                          </p>
                          <p>
                            As you make decisions in each category, you may find
                            yourself revisiting decisions you made earlier in
                            the process.
                          </p>
                        </>
                      }
                      items={[
                        {
                          icon: <House />,
                          label: 'Home Needs',
                          content: `${formatDealerName(
                            dealer?.dealerName
                          )} offer floor plans, features and upgrades to fit almost any lifestyle. Define the list of wants and needs for your future home to help find the best model for you.`,
                        },
                        {
                          icon: <PieGraph />,
                          label: 'Budget',
                          content:
                            'Knowing how much home you can afford, including the costs that come with buying a home, can help you hone in on the home that fits your needs and your budget.',
                        },
                        {
                          icon: <Clock />,
                          label: 'Timeline',
                          content:
                            'Your timeline can be impacted by budget, land status and home availability. If you are anxious to move into your home, consider a home that is available at your local home center.',
                        },
                        {
                          icon: <Mountains />,
                          label: 'Land',
                          content:
                            'Our goal is to help you avoid as many unexpected timeline delays related to land purchase and prep as possible. Know what questions to ask and what you can expect during the construction process ahead of time.',
                        },
                      ]}
                    />
                  </div>
                </InView>
              )}

              <InView
                triggerOnce
                as="section"
                className="accordion-section"
                id="accordionSection"
                threshold={0.3}
                onChange={(inView, entry) =>
                  inView
                    ? dxContext?.actions?.markContentComplete(
                        journeyPlannerProgressObj,
                        'home_buying_journey_faq'
                      )
                    : null
                }
              >
                <div className="container">
                  <Accordion
                    heading="FAQs"
                    accordions={accordionContent.map((accordion) => ({
                      ...accordion,
                      onExpand: () => {
                        fireSegmentEvent('Accordion Expanded', {
                          item: accordion.title,
                          type: 'details_expanded_home_buying_journey_faq',
                          defaultOpen: false,
                          autoClosing: false,
                          eventContext: 'portal_home_buying_journey_faqs',
                        });
                      },
                    }))}
                  />
                </div>
              </InView>
              <section className="explore-section">
                <div className="container">
                  <ItemsGrid
                    heading="Next Step"
                    items={[
                      {
                        children: {
                          icon: <PieChart />,
                          chip: {
                            text: 'BUDGET',
                            background: '#DAFEFD',
                            color: 'rgba(37, 41, 45, 0.5)',
                          },
                          heading: 'Establish your budget',
                        },
                      },
                    ]}
                    components={{
                      Item: BudgetLink,
                    }}
                  />
                </div>
              </section>
            </>
          )}
        </HomeBuyingJourneyStyles>
      </App>
    </ThemeProvider>
  );
};

HomeBuyingJourney.getInitialProps = async (ctx) => {
  return await parseInitialPropsContext(ctx, dealerService, homesService).catch(
    (e) => {
      console.error(e);
      return { redirectTo: '/error' };
    }
  );
};

export default HomeBuyingJourney;
